const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001'

export interface Resistance {
  id: string
  domain: string
  daily_limit: number
  session_duration_minutes: number
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface Session {
  id: string
  resistance_id: string
  reason_text: string
  started_at: string
  ended_at?: string
  duration_seconds?: number
}

export interface AnalyticsData {
  sessionsByDate: Record<string, {
    id: string
    domain: string
    startedAt: string
    endedAt?: string
    duration: number
    reason: string
  }[]>
  dailyStats: {
    date: string
    totalSessions: number
    totalDuration: number
    domains: Record<string, { sessions: number; duration: number }>
  }[]
  totalSessions: number
  dateRange: { start: string; end: string }
}

export interface TrendsData {
  trends: {
    period: string
    sessions: number
    totalDuration: number
    domains: string[]
  }[]
  period: 'week' | 'month'
}

export interface ResistanceAnalytics {
  totalSessions: number
  totalDuration: number
  averageDuration: number
  sessionsByDate: Record<string, number>
  durationByDate: Record<string, number>
  recentSessions: {
    id: string
    startedAt: string
    endedAt?: string
    duration: number
    reason: string
  }[]
  period: string
}

class ApiClient {
  private getAuthHeaders(): HeadersInit {
    // This will be called with the session token when needed
    return {
      'Content-Type': 'application/json',
    }
  }

  private async getAuthHeadersWithToken(): Promise<HeadersInit> {
    // Import supabase here to avoid circular dependency
    const { supabase } = await import('./supabase')
    const { data: { session } } = await supabase.auth.getSession()
    
    console.log('API: Getting auth headers, session exists:', !!session, 'token exists:', !!session?.access_token)
    
    if (!session?.access_token) {
      throw new Error('No authentication token available')
    }
    
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`
    }
  }

  async getResistances(): Promise<Resistance[]> {
    const headers = await this.getAuthHeadersWithToken()
    console.log('API Headers:', headers)
    
    const response = await fetch(`${API_BASE}/resistances`, {
      headers
    })
    
    console.log('Response status:', response.status)
    
    if (!response.ok) {
      const errorText = await response.text()
      console.error('API Error:', errorText)
      throw new Error(`Failed to fetch resistances: ${response.status} ${errorText}`)
    }
    
    const data = await response.json()
    console.log('API Response:', data)
    return data.resistances
  }

  async createResistance(resistance: Omit<Resistance, 'id' | 'created_at' | 'updated_at'>): Promise<Resistance> {
    console.log('API: Creating resistance with data:', resistance)
    
    const headers = await this.getAuthHeadersWithToken()
    console.log('API: Using headers:', headers)
    
    const response = await fetch(`${API_BASE}/resistances`, {
      method: 'POST',
      headers,
      body: JSON.stringify(resistance)
    })
    
    console.log('API: Create response status:', response.status)
    
    if (!response.ok) {
      const errorText = await response.text()
      console.error('API: Create error response:', errorText)
      throw new Error(`Failed to create resistance: ${response.status} ${errorText}`)
    }
    
    const data = await response.json()
    console.log('API: Create response data:', data)
    return data.resistance
  }

  async updateResistance(id: string, updates: Partial<Resistance>): Promise<Resistance> {
    const response = await fetch(`${API_BASE}/resistances/${id}`, {
      method: 'PUT',
      headers: await this.getAuthHeadersWithToken(),
      body: JSON.stringify(updates)
    })
    
    if (!response.ok) {
      throw new Error('Failed to update resistance')
    }
    
    const data = await response.json()
    return data.resistance
  }

  async deleteResistance(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/resistances/${id}`, {
      method: 'DELETE',
      headers: await this.getAuthHeadersWithToken()
    })
    
    if (!response.ok) {
      throw new Error('Failed to delete resistance')
    }
  }

  async getTodaysSessions(resistanceId: string): Promise<{ opensToday: number }> {
    const response = await fetch(`${API_BASE}/sessions/today/${resistanceId}`, {
      headers: await this.getAuthHeadersWithToken()
    })
    
    if (!response.ok) {
      throw new Error('Failed to fetch today\'s sessions')
    }
    
    return response.json()
  }

  async getAnalytics(startDate?: string, endDate?: string): Promise<AnalyticsData> {
    const params = new URLSearchParams()
    if (startDate) params.append('startDate', startDate)
    if (endDate) params.append('endDate', endDate)
    // Add timezone offset in minutes
    params.append('timezoneOffset', new Date().getTimezoneOffset().toString())
    
    const response = await fetch(`${API_BASE}/sessions/analytics?${params.toString()}`, {
      headers: await this.getAuthHeadersWithToken()
    })
    
    if (!response.ok) {
      throw new Error('Failed to fetch analytics data')
    }
    
    return response.json()
  }

  async getTrends(period: 'week' | 'month' = 'week'): Promise<TrendsData> {
    const params = new URLSearchParams()
    params.append('period', period)
    // Add timezone offset in minutes
    params.append('timezoneOffset', new Date().getTimezoneOffset().toString())
    
    const response = await fetch(`${API_BASE}/sessions/analytics/trends?${params.toString()}`, {
      headers: await this.getAuthHeadersWithToken()
    })
    
    if (!response.ok) {
      throw new Error('Failed to fetch trends data')
    }
    
    return response.json()
  }

  async getResistanceAnalytics(resistanceId: string, days: number = 30): Promise<ResistanceAnalytics> {
    const params = new URLSearchParams()
    params.append('days', days.toString())
    // Add timezone offset in minutes
    const timezoneOffset = new Date().getTimezoneOffset().toString()
    params.append('timezoneOffset', timezoneOffset)
    
    console.log('🚀 Frontend: Making resistance analytics API call with timezone offset:', timezoneOffset)
    
    const response = await fetch(`${API_BASE}/sessions/analytics/${resistanceId}?${params.toString()}`, {
      headers: await this.getAuthHeadersWithToken()
    })
    
    if (!response.ok) {
      throw new Error('Failed to fetch resistance analytics')
    }
    
    return response.json()
  }

  async endSession(sessionId: string, endReason: string = 'manual'): Promise<{ success: boolean; durationSeconds: number }> {
    const response = await fetch(`${API_BASE}/sessions/${sessionId}/end`, {
      method: 'PUT',
      headers: await this.getAuthHeadersWithToken(),
      body: JSON.stringify({ endReason })
    })
    
    if (!response.ok) {
      throw new Error('Failed to end session')
    }
    
    return response.json()
  }
}

export const api = new ApiClient()